import React, { useRef, useState, useEffect } from "react";
import {
    TableProvider,
    ConnectedTable,
    ConnectedTableLayout,
    ConnectedTableFilter,
    HeaderTitle,
} from "@panwds/react-table";
import { useFilters } from "@panwds/react-filters";
import { TableProps } from "../../types";
import { applyFilterToData } from "../../utils/utils";
import { makeStyles } from '@material-ui/core';
import { LoadingIcon, Tooltip } from "@panwds/react-ui";
import { InfoIcon } from "@panwds/icons";
import { TableFilterBar } from "./TableFilterBar";
import { BatchActions } from "./TableBatchActions";
import { isEmpty, isEqual } from 'lodash';

const useStyles = makeStyles((theme) => ({
    paginatedScrollTableHeight: (props: any) => ({
        overflowY: 'auto',
        overflowX: 'auto',
        height: `${props.tableOffsetHeight ? 'calc(100vh - ' + props.tableOffsetHeight + 'px)' : '100vh'}`
    }),
}));

// Helper function to clean filters object by removing empty/undefined values
const cleanFilters = (filters: any) => {
    if (!filters || typeof filters !== 'object') {
        return {};
    }

    const cleanedFilters: any = {};

    Object.entries(filters).forEach(([key, value]) => {
        // Check if the filter value is meaningful
        const hasValue = value !== undefined &&
                         value !== null &&
                         value !== '' &&
                         !(Array.isArray(value) && value.length === 0) &&
                         !(typeof value === 'object' && isEmpty(value));

        if (hasValue) {
            cleanedFilters[key] = value;
        }
    });

    return cleanedFilters;
};

// Helper function to check if filters object is effectively empty
const hasActiveFilters = (filters: any) => {
    const cleaned = cleanFilters(filters);
    return !isEmpty(cleaned);
};

export const Table = ({ ...props }: TableProps) => {
    const { filters } = useFilters("FilterAndTable");
    const tableInstanceRef = useRef<any>();
    const [filteredData, setFilteredData] = useState(props.tableComponentData || []);
    const previousFiltersRef = useRef({});

    const classes = useStyles({ tableOffsetHeight: props.tableOffsetHeight });
    // Apply incremental filtering when filters change
    useEffect(() => {
        const sourceData = props.tableComponentData;

        if (!sourceData || !Array.isArray(sourceData)) {
            console.warn("Invalid table component data:", sourceData);
            setFilteredData([]);
            return;
        }

        // Clean the filters to remove empty values
        const cleanedFilters = cleanFilters(filters);

        // If no active filters, reset to original data
        if (!hasActiveFilters(filters)) {
            setFilteredData(sourceData);
            previousFiltersRef.current = {};
            return;
        }

        // Check if any filter config has useAndLogic
        const hasAndLogicFilters = Object.keys(cleanedFilters).some(filterProp => {
            const filterConfig = props.tableComponentFilterBar?.filterConfig?.find(
                (item: any) => item.name === filterProp
            );
            return filterConfig?.useAndLogic === true;
        });

        // Determine filtering strategy
        if (hasAndLogicFilters) {
            // Only do incremental filtering if filters actually changed
            if (!isEqual(cleanedFilters, cleanFilters(previousFiltersRef.current))) {
                // For AND logic, we need to be more careful about incremental updates
                // For now, restart from source to ensure correctness
                let newData = [...sourceData];

                Object.entries(cleanedFilters).forEach(([filterProp, filterValues]: any) => {
                    newData = applyFilterToData(newData, filterProp, filterValues, props.tableComponentFilterBar?.filterConfig || []);
                });

                setFilteredData(newData);
            }
        } else {
            // For OR logic, always filter from source data
            let newData = [...sourceData];

            Object.entries(cleanedFilters).forEach(([filterProp, filterValues]: any) => {
                newData = applyFilterToData(newData, filterProp, filterValues, props.tableComponentFilterBar?.filterConfig || []);
            });

            setFilteredData(newData);
        }

        previousFiltersRef.current = cleanedFilters;

    }, [filters, props.tableComponentData, props.tableComponentFilterBar?.filterConfig]);
    // Reset filtered data when original data changes
    useEffect(() => {
        if (!hasActiveFilters(filters)) {
            setFilteredData(props.tableComponentData || []);
        }
    }, [props.tableComponentData]); // Removed filters from dependencies to avoid loop

    return (
        <div className={props?.tableReadOnly ? "readOnly" : ""} data-testid="common-table-component">
            <div className="tw-p-2 tw-bg-white dark:tw-bg-gray-800 tw-text-gray-900 dark:tw-text-gray-100">
                <TableProvider
                    columns={props.tableComponentColumns}
                    data={filteredData}
                    enableColumnSort
                    enableResizeColumns
                    enableRowSelect={props?.tableEnableRowSelect || false}
                    initialState={{
                        groupBy: props?.tableInitialGroupBy || [],
                    }}
                    isLoading={props?.tableIsLoading || false}
                    tableInstanceRef={tableInstanceRef}
                >
                    {props.tableComponentFilterBar?.filterConfig && (
                        <TableFilterBar {...{
                            ...props,
                            tableInstanceRef: tableInstanceRef.current,
                            originalData: props.tableComponentData
                        }}/>
                    )}
                    <ConnectedTableLayout
                        dataTestId="table-min-height-with-sidebar"
                        sidecar={<div />}
                        overflow
                        enableHeaderTitle
                        resultsSummary={!props.tableEnableRowSelect ? <div style={{
                            paddingTop: '0.5rem',
                            paddingBottom: '0.5rem',
                            paddingLeft: '1rem',
                            paddingRight: '1rem'
                        }} /> : undefined}
                        controlLeft={
                            <>
                                {props?.tableTitle && (
                                    <div className="tw-flex tw-items-center tw-gap-2">
                                        <HeaderTitle
                                            title={`${props.tableTitle} (${filteredData?.length || 0})`}
                                        />
                                        {props?.tableSubtitle && (
                                            <Tooltip label={props.tableSubtitle} defaultPlacement="right">
                                                <InfoIcon size="xs" />
                                            </Tooltip>
                                        )}
                                    </div>
                                )}
                                {props?.tableisBackgroundFetching && (<LoadingIcon className="tw-ml-4 tw-animate-spin" />)}
                                {/* Left batch actions */}
                                {props?.tableBatchAction && (
                                    <BatchActions position="left">
                                        {(selectedRows, tableInstance) => {
                                            const batchAction = props.tableBatchAction?.(selectedRows, tableInstance);
                                            return batchAction?.leftControl || null;
                                        }}
                                    </BatchActions>
                                )}
                            </>
                        }
                        controlRight={
                            <>
                                {!props.tableComponentFilterBar?.filterConfig && <ConnectedTableFilter />}

                                {/* Right batch actions */}
                                {props?.tableBatchAction && (
                                    <BatchActions position="right">
                                        {(selectedRows, tableInstance) => {
                                            const batchAction = props.tableBatchAction?.(selectedRows, tableInstance);
                                            return batchAction?.rightControl || null;
                                        }}
                                    </BatchActions>
                                )}
                            </>
                        }
                    >
                        <div className={(props.tableOffsetHeight) ? classes.paginatedScrollTableHeight : ""}>
                            <ConnectedTable
                                emptyStateProps={
                                    props.tableComponentEmptyStateProps &&
                                    props.tableComponentEmptyStateProps
                                }
                                appearance="primary"
                                rowSize={props.tableRowSize || "standard"}
                            />
                        </div>
                    </ConnectedTableLayout>
                </TableProvider>
            </div>
        </div>
    );
};
